import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, ViewChild } from '@angular/core';
import { lockClick } from 'directives/cs-capture-cover/cs-capture-cover.directive';
import { LL } from 'i18n/i18n.service';
import { TranslatedString } from 'models/translated-string';
import { describeExerciseState } from 'util/formatting';
import { ImageService } from 'services/image-service';
import { UserDb } from 'services/user-db.service';
import { DebugService } from 'services/debug.service';
import { CsPage, NavController } from 'providers/nav-controller';
import { AnyExercise, CustomExercise, Exercise, ExerciseDbService, ExerciseSlot, isCustomExercise, isForkUde } from 'services/exercise-db.service';
import { resolveLocalCustomExerciseAsync } from 'services/exercise-fork-update';
import { isPouchNotFound } from '@cf-shared/data-model/pouch';
import { CoachForkUpdateFlow } from 'services/exercise-fork-update-flow';
import { ExerciseDiffResult } from 'pages/exercise-diff/exercise-diff-page-interface';
import { SettingsService } from 'services/settings.service';
import { CoachExerciseSyncService } from 'services/coach-exercise-sync.service';
import { Subscription } from 'rxjs';
import { ToastController } from 'providers/toast-controller';
import { ToastType } from 'components/toast/toast-host';
import { addExerciseToWorkoutAsync, findNearestLiveAncestorAsync, suggestAvailableExercisesAsync } from 'services/exercise-switching';
import { IExerciseConfigurationPage } from 'pages/exercise-configuration/exercise-configuration-interface';
import { TelemetryService } from 'services/telemetry.service';
import { FitnessPlan, Plan_SubSlot, Plan_Workout, findOldSubslot, otherWorkoutsSharingExercise, workoutAlreadyListsExercise } from '@cf-shared/data-model/fitness-plan';
import { LoggingService } from 'services/logging.service';
import { sleep } from '@cf-shared/shared-util';
import { LexicalCandidate, prepareLexicalCandidate, prepareLexicalQuery, scoreLexicalMatch } from '@cf-shared/exercise-lexical-matcher';
import { DatabaseWrapper , DocWatcher } from 'services/database-wrapper';
import { ButtonType } from '@cf-shared/enums';
import { PopupMenuButton, PopupMenuProvider } from 'providers/popup-menu/popup-menu';
import { deepOverwrite, deepClone } from '@cf-shared/deep-helpers';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { ExerciseStatStateDescription, FitnessState, ExerciseStat } from '@cf-shared/data-model/user-state';
import { IExerciseStatsPage } from 'pages/exercise-stats/exercise-stats-interface';
import { IHelpCardPage } from 'pages/help-card-page/help-card-page-interface';
import { IEditExercisePage } from 'pages/edit-exercise/edit-exercise-interface';
import { ModalPageMarker } from 'providers/modal-page-marker';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { computeAuthorIdentityHashAsync } from 'util/author-identity-hash';
import { buildHiddenUserUdesByCoach } from 'services/coach-picker-hide';
import { resolveFormerCoachHashesAsync } from 'services/former-coach-resolver';
import { DEFAULT_COACH_NAME_FALLBACK } from '@cf-shared/data-model/coach';
import { ExerciseSwitchPageParams } from './exercise-switch-interface';
import { isDatabaseTornDown, isDatabaseUnavailable } from 'services/database-unavailable-error';
import { describeErrorForLog } from '@cf-shared/error-describe';
import { resolveAnyExerciseByIdAsync } from 'services/exercise-resolve';
import { SearchOperationTracker, TrackedSearchOperation } from 'util/search-operation-tracker';

interface ExerciseSwitchUIOption {
  exercise: AnyExercise;
  selected: boolean;
  stateStateDesc?: ExerciseStatStateDescription<TranslatedString>;
  workout?: Plan_Workout;
  lexical?: LexicalCandidate;  // prepared name texts, used for searching
  suggestedIndex?: number;
  // Phase 2 of #3198 — coach-authored UDE: shown with a "Coach: <name>" badge so the
  // client can tell at a glance which custom exercises were delivered by their coach.
  // Authoritative check is `authorIdentityHash === computeAuthorIdentityHash(coachUid)`
  // (same identity-hash used by the picker hide-policy in `coach-picker-hide.ts`).
  isCoachAuthored?: boolean;
  // Display name for the badge tooltip; pulled from `coach-relationship/as-client.coachName`
  // once per picker render, so the badge stays in sync if the coach renames themselves.
  coachDisplayName?: string;
  // Phase 3 of #3198 — a genuine source update is available for this imported fork
  // (`checkImportedExerciseForUpdateAsync().hasUpdate`). Detected async after the rows
  // build (fire-and-forget); drives the dot next to the exercise name and the "Update"
  // vs "Check for Update" menu label. False after a partial apply / ignore (the rev is
  // marked seen), so a resolved fork shows only the always-available "Check for Update".
  hasUpdateAvailable?: boolean;
  // Phase 3 of #3198 — UDE was authored by a coach the user has since detached from.
  // Drives a "Former coach" badge + tooltip. Mutually exclusive with isCoachAuthored:
  // the resolver excludes any formerCoaches entry whose coachUid matches the active
  // coach, so a re-claim during a sync-lag window doesn't render both badges at once.
  isFromFormerCoach?: boolean;
  // Display name of the former coach for the tooltip; pulled from the matching
  // `formerCoaches[*].coachName`.
  formerCoachName?: string;
}

interface SearchOperation extends TrackedSearchOperation {
  foundItems: ExerciseSwitchUIOption[];
}

@Component({
    selector: 'page-exercise-switch',
    templateUrl: 'exercise-switch.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
    providers: [{ provide: ModalPageMarker, useValue: new ModalPageMarker({ width: 675 }) }]
})
export class ExerciseSwitchPage extends CsPage<ExerciseSwitchPageParams> {
  protected LL = LL;
  // global data that needs loading at start...
  protected allSlots: ExerciseSlot[] = [];

  // used to select exercise on reload when coming back from editing/creating
  selectExerciseByIdWhenLoading: string = undefined;

  // input params
  pageParams: ExerciseSwitchPageParams = undefined;

  // --- things prepared from input params ---
  // local params - accessed via navParams.data.local
  fitnessPlan: FitnessPlan;
  fitnessState: FitnessState;
  workout: Plan_Workout;
  exercise?: Exercise;
  planSubSlot?: Plan_SubSlot;

  // --- sub-lists to make up the currently visible list (according to filters) ---
  allExerciseUIOptions: ExerciseSwitchUIOption[] = [];  // all exercises that are available
  customUIOptions: ExerciseSwitchUIOption[] = [];
  // Built at picker load alongside the hide-source set: maps each forked source id →
  // fork UDE. Lets the customs filter and recommendation marking promote a fork into
  // its (now-hidden) source's place in slot-bound tabs (Recommended / per-slot).
  private forkBySourceId: Map<string, CustomExercise> = new Map();

  isCustomExercise = isCustomExercise;

  // --- filters and search parameters ---
  protected filterShowOnlyCustom = false;
  protected filterBySlotId: string = undefined;
  protected filterBySlotText: string = 'All'; // just the text to show in the UI

  // Dropdown category filter - special values: 'all', 'recommended', 'myExercises', or slotId
  protected selectedCategoryId: string = 'recommended';

  // Tracks whether the user's category selection should be preserved across `ionViewDidEnter`
  // re-runs (return from edit-exercise / others). Set after the first entry's default-init
  // block runs; checked before re-applying defaults on subsequent entries.
  private categorySelectionInitialized = false;

  // Set on Modify / fork-on-edit entry when the tapped row was the currently-selected one.
  // Read by `onExerciseModified` to decide whether to auto-select the edited/forked exercise
  // on return. If the user didn't have the row selected when they opened edit-exercise, we
  // don't want returning from edit-exercise to suddenly select it.
  private wasSelectedBeforeEdit = false;

  protected prevSearchText = '';
  protected searchText = '';
  protected showSpinner = true;
  protected searchDebouncerTimeout: ReturnType<typeof setTimeout> = undefined;
  protected isInitializing = true; // Prevent interaction during initial load

  // Selected item ID for radio group
  protected selectedItemId: string = undefined;

  // the final list after filtering and searching
  protected displayedUIOptions: ExerciseSwitchUIOption[] = [];

  protected numSearchMatchesHidden: number; // number items that match the search but are hidden due to filters

  private docWatcher: DocWatcher;
  // Live "update available" refresh: subscribe to the coach-exercise apply pipeline so a
  // coach edit (a same-device local write that bypasses the cross-device DocWatcher) still
  // refreshes the dot / menu without an app reload.
  private coachAppliedSub: Subscription | null = null;
  // The check-for-update / diff-picker / apply sequence, shared with exercise-stats (the other
  // screen that applies at the canonical-doc level). Constructed below.
  private forkUpdateFlow: CoachForkUpdateFlow;

  // Resolves a fork's source from the user's OWN DB so a same-user local source isn't
  // reported as a deleted shared exercise (#3808). Passed into checkImportedExerciseForUpdateAsync.
  private resolveLocalSource = (id: string) => resolveLocalCustomExerciseAsync(this.userDb, id);

  @ViewChild('searchInput', { read: ElementRef }) searchInputRef: ElementRef;

  constructor(
    public navCtrl: NavController,
    protected userDb: UserDb,
    protected imageService: ImageService,
    protected debug: DebugService,
    protected exerciseDb: ExerciseDbService,
    private telemetry: TelemetryService,
    private logging: LoggingService,
    private cd: ChangeDetectorRef,
    private popupMenuCtrl: PopupMenuProvider,
    private alertCtrl: AlertController,
    private cloudFunctionsService: CloudFunctionsService,
    private toastController: ToastController,
    private settings: SettingsService,
    private coachExerciseSync: CoachExerciseSyncService,
  ) {
    super();
    this.forkUpdateFlow = new CoachForkUpdateFlow({
      pageName: 'exercise-switch',
      navCtrl: this.navCtrl,
      userDb: this.userDb,
      exerciseDb: this.exerciseDb,
      toastController: this.toastController,
      logging: this.logging,
      settings: this.settings,
      getDocWatcher: () => this.docWatcher,
      isViewDestroyed: () => this.viewDestroyed,
      // this page has both exercise lists already loaded, so a similar-exercise id resolves
      // to a real name here.
      resolveSimilarExerciseName: id => {
        if (!id) return LL.editExercise.alerts.updateAvailable.empty();
        const ex = this.allExerciseUIOptions.find(o => o.exercise._id === id)?.exercise
          || this.customUIOptions.find(o => o.exercise._id === id)?.exercise;
        return ex?.name || id;
      },
    });
  }

  isParamsValid(params: ExerciseSwitchPageParams): boolean {
    // local params accessed via data.local
    const fitnessPlan = this.navParams.data.local?.fitnessPlan;
    const fitnessState = this.navParams.data.local?.fitnessState;
    if (params.workoutId && fitnessPlan && fitnessState && params.returnCallback) {
      return true;
    } else {
      return false;
    }
  }

  async ionViewCanEnter() {
    if (!this.docWatcher) {
      this.docWatcher = this.userDb.database.createDocWatcher((_changes) => {
        if (this.viewDestroyed) return;
        this.cd.markForCheck();
      });
    }
    if (!this.coachAppliedSub) {
      // a coach edit flows through coach-exercise-sync's apply pipeline (local write,
      // invisible to the DocWatcher) — re-detect the "update available" state live.
      this.coachAppliedSub = this.coachExerciseSync.coachExerciseApplied$.subscribe(() => {
        if (this.viewDestroyed) return;
        this.scheduleUpdateAvailableRedetect();
      });
    }

    // grab the params
    this.pageParams = await this.navCtrl.getPageParamsSafe(this);
  }

  // Check if the recommended filter is selected (for showing info alert)
  isRecommendedFilterSelected(): boolean {
    return this.selectedCategoryId === 'recommended' && !this.searchText.trim();
  }

  // Get the recommended slot ID (the slot of the exercise being replaced)
  getRecommendedSlotId(): string | undefined {
    return this.planSubSlot?.slotId;
  }

  // Handle dropdown category change
  handleCategoryChange(categoryId: string) {
    this.selectedCategoryId = categoryId;

    // Map category to filter settings
    if (this.selectedCategoryId === 'all') {
      this.filterBySlotId = undefined;
      this.filterShowOnlyCustom = false;
    } else if (this.selectedCategoryId === 'recommended') {
      this.filterBySlotId = this.getRecommendedSlotId();
      this.filterShowOnlyCustom = false;
    } else if (this.selectedCategoryId === 'myExercises') {
      this.filterBySlotId = undefined;
      this.filterShowOnlyCustom = true;
    } else {
      // It's a slot ID
      this.filterBySlotId = this.selectedCategoryId;
      this.filterShowOnlyCustom = false;
    }

    this.cd.markForCheck();
    void this.searchAsync(this.searchText);
  }

  // Badge text for coach-authored UDEs (Phase 2 of #3198). Short label fits the existing
  // badge slot next to the "Active" indicator. Tooltip names the coach when the display
  // name is known; falls back to a generic phrase if the relationship doc lacks coachName
  // (legacy claims pre-Phase-2 may carry only coachUid).
  protected coachBadgeLabel(): TranslatedString {
    return LL.exerciseSwitch.coachBadge.label();
  }
  protected coachBadgeInfo(item: ExerciseSwitchUIOption): TranslatedString {
    const name = item.coachDisplayName?.trim();
    return name
      ? LL.exerciseSwitch.coachBadge.info({ coachName: name })
      : LL.exerciseSwitch.coachBadge.infoNoName();
  }

  handleRowClick(item: ExerciseSwitchUIOption) {
    if (this.isInitializing) return;
    this.setSelectedItem(item);
    this.selectedItemId = item.exercise._id;
  }

  // Handle ellipsis menu click
  async handleMoreOptions(event: Event, item: ExerciseSwitchUIOption) {
    event.stopPropagation();

    const buttons: PopupMenuButton[] = [];

    buttons.push({
      text: LL.exerciseSwitch.buttons.statsAndNotes(),
      handler: () => this.handleStatsAndNotesButtonClick(item),
    });

    // if has description or video
    if (item.exercise?.description || item.exercise?.videoUrl) {
      buttons.push({
        text: LL.exerciseSwitch.buttons.description(),
        handler: () => this.handleDescriptionButtonClick(item),
      });
    }

    // if custom exercise
    if (isCustomExercise(item.exercise)) {
      buttons.push({
        text: LL.exerciseSwitch.buttons.modify(),
        handler: () => this.handleModifyExerciseButtonClick(item),
      });
      buttons.push({
        text: LL.exerciseSwitch.buttons.share(),
        handler: () => this.handleShareExerciseFromList(item),
      });
      // R3 — update / re-check menu item for any imported fork (has `sourceExerciseId`,
      // not a coach copy). Always offered: "Update" when a genuinely-new coach rev is
      // available (`hasUpdateAvailable`), else "Check for Update" as a manual re-pull of
      // the source. A partial apply / ignore is treated as RESOLVED — there is no separate
      // "pending" state; the prominent "Update" label (+ the row dot) returns only when
      // the coach pushes a new rev. Coach copies are excluded: `coachShared` gates them out
      // of update checks; a fork of a coach copy has no `coachShared` and does carry
      // `sourceExerciseId`.
      if (item.exercise.local?.sourceExerciseId && !item.exercise.local?.coachShared) {
        buttons.push({
          text: item.hasUpdateAvailable
            ? LL.exerciseSwitch.buttons.update()
            : LL.exerciseSwitch.buttons.checkForUpdate(),
          handler: () => void this.handleCheckForUpdate(item),
        });
      }
      buttons.push({
        text: LL.exerciseSwitch.buttons.delete(),
        handler: () => void this.handleDeleteExerciseButtonClick(item),
      });
    } else {
      // global exercise: Modify forks-on-edit
      buttons.push({
        text: LL.exerciseSwitch.buttons.modify(),
        handler: () => this.handleForkOnEditButtonClick(item),
      });
    }

    await this.popupMenuCtrl.createMenu({
      buttons,
      targetFromElement: event.target as HTMLElement,
    });
  }

  handleSearchChanged() {
    /* Start showing spinner immediately when user starts typing, even though we start the search only after the debounce
     This makes the UI look more responsive. */
    this.showSpinner = true;
    this.cd.markForCheck();

    // debounce the input typing to prevent too many requests to the databases
    if (this.searchDebouncerTimeout) {
      clearTimeout(this.searchDebouncerTimeout);
    }
    // If we are deleting the search text
    if (this.searchText === '' && this.prevSearchText.length > 1) {
      this.telemetry.trackEvent('equipment_search_rejected', { searchText: this.prevSearchText });
    }
    this.prevSearchText = this.searchText;
    let text = this.searchText || '';
    this.searchDebouncerTimeout = setTimeout(() => {
      void this.searchAsync(text);
    }, 300);
  }

  private searchOps = new SearchOperationTracker<SearchOperation>();

  async searchAsync(searchText: string) {
    // separate into two scopes to prevent accidentally using untrimmed searchText
    // This can easily happen due to autocomplete and can cause subtle bugs that are hard to find.
    return this.searchAsync_internal(searchText.trim());
  }

  async searchAsync_internal(trimmedText: string) {
    let op = this.searchOps.start(trimmedText, { foundItems: [] });
    if (op === undefined) {
      return;
    }

    this.showSpinner = true;
    this.cd.markForCheck();

    if (this.searchOps.shouldCancel(op)) {
      this.searchOps.end(op);
      return;
    }

    // prepared once, scored against every option below (shared matcher — also used by
    // the workout-plan scanner's fallback matching; the picker scores names only, so
    // search behavior is unchanged by the extraction)
    let query = prepareLexicalQuery(trimmedText);

    // collect all viable options
    let optionsToSearch = [];

    const isSlotBound = !!this.filterBySlotId && !(this.selectedCategoryId === 'recommended' && trimmedText);

    // collect filtered customs first (used in both slot-bound and unbound paths). In
    // slot-bound tabs, also surface forks whose source is one of the slot's defined
    // alternatives — the source itself is hidden by the picker filter, so the fork
    // stands in for it.
    let filteredCustomUIOptions = this.customUIOptions;
    let slotExerciseIds = new Set<string>();
    if (isSlotBound) {
      const filterSlot = this.allSlots.find(s => s._id === this.filterBySlotId);
      slotExerciseIds = new Set(filterSlot?.exercises.map(e => e.exerciseId) ?? []);
      filteredCustomUIOptions = filteredCustomUIOptions.filter(e => {
        const ude = e.exercise as CustomExercise;
        if (ude.usedInSlots.includes(this.filterBySlotId)) return true;
        // for a fork, treat its source membership as the fork's membership
        return isForkUde(ude) && slotExerciseIds.has(ude.local.sourceExerciseId);
      });
    }

    // if not showing only custom
    if (!this.filterShowOnlyCustom) {
      // if showing all, or if recommended + active search (search all exercises when typing)
      if (!isSlotBound) {
        optionsToSearch = optionsToSearch.concat(this.allExerciseUIOptions);

        // if filtered by explicit slot selection
      } else {
        // add globals matching the slot filter, and customs together — customs include
        // forks of slot-relevant globals (which carry suggestedIndex stamped at load).
        // Sorting after BOTH lists are merged is what lets a fork land in its source's
        // recommendation position.
        optionsToSearch = optionsToSearch.concat(this.allExerciseUIOptions.filter(e =>
          e.exercise && slotExerciseIds.has(e.exercise._id)));
        optionsToSearch = optionsToSearch.concat(filteredCustomUIOptions);
        // if the slot is suggested slot
        if (this.planSubSlot && this.planSubSlot.slotId === this.filterBySlotId) {
          // sort the options by suggested index, and all without index go at the end
          optionsToSearch.sort((a, b) => {
            let aSuggested = a.suggestedIndex !== undefined;
            let bSuggested = b.suggestedIndex !== undefined;
            if (aSuggested && bSuggested) {
              return a.suggestedIndex - b.suggestedIndex;
            } else if (aSuggested) {
              return -1;
            } else if (bSuggested) {
              return 1;
            } else {
              return 0;
            }
          });
        }
      }
    }

    // unbound paths haven't added customs yet — append them now
    if (!isSlotBound || this.filterShowOnlyCustom) {
      optionsToSearch = optionsToSearch.concat(filteredCustomUIOptions);
    }

    let scoredOptions: { option: ExerciseSwitchUIOption, score: number }[] = [];
    for (let e of optionsToSearch) {
      // shared trigram + substring + word scoring; undefined means below the cutoff
      let score = scoreLexicalMatch(query, e.lexical);
      if (score === undefined) continue;
      scoredOptions.push({ option: e, score });
    }

    scoredOptions.sort((a, b) => b.score - a.score);
    let foundUIOptions = scoredOptions.map(s => s.option);

    op.foundItems = foundUIOptions;

    this.displayedUIOptions = op.foundItems;
    this.numSearchMatchesHidden = optionsToSearch.length - foundUIOptions.length;

    this.showSpinner = false;
    this.cd.markForCheck();
    this.searchOps.end(op);

    await this.scrollSelectedIntoViewAsync();
  }

  async scrollSelectedIntoViewAsync() {
    await sleep(1); // wait for the UI to update

    let selectedItem = this.getSelectedItem();
    if (selectedItem) {
      let itemId = `item-${selectedItem.exercise._id}`;
      let item = document.getElementById(itemId);
      if (item) {
        item.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }
    }
  }

  async ionViewDidEnter() {
    // local params - access via data.local
    this.fitnessPlan = this.navParams.data.local?.fitnessPlan;
    this.fitnessState = this.navParams.data.local?.fitnessState;

    // load global data that will be needed
    this.allSlots = await this.exerciseDb.getAllSlotsAsync(this.docWatcher);
    // remove obsolete slots that might be stuck in the database - those that are actually subslots (thus unused)
    // we recognize them by the fact that their id ends with a number
    this.allSlots = this.allSlots.filter(s => s._id.match(/\d+$/) === null);

    // convert params from string to actual objects
    if (this.pageParams.exerciseId) {
      this.exercise = await resolveAnyExerciseByIdAsync(this.pageParams.exerciseId, this.exerciseDb, this.userDb, this.docWatcher);
    }
    if (this.pageParams.subSlotId) {
      this.planSubSlot = this.fitnessPlan.subSlots[this.pageParams.subSlotId]
    }
    let workoutIndex = this.pageParams.workoutId ? (this.pageParams.workoutId.charCodeAt(0) - 'A'.charCodeAt(0)) : -1;
    this.workout = this.fitnessPlan.workouts[workoutIndex];
    if (!this.workout) {
      this.logging.trackAbnormalError(LL.exerciseSwitch.errors.workoutNotFound({ workoutId: this.pageParams.workoutId }));
      void this.navCtrl.popAsync();
      return;
    }

    // Apply default category selection only on the first entry. On re-entry (return from
    // edit-exercise / onExerciseModified rebuild) preserve whatever the user picked. Note
    // that `allSlots` is fresh from this call so the slot membership check below stays valid.
    if (!this.categorySelectionInitialized) {
      if (this.planSubSlot) {
        // if the slot exists and is not empty
        let slot = this.allSlots.find(s => s._id === this.planSubSlot.slotId);
        if (slot && slot.exercises && slot.exercises.length > 0) {  // in March 2025 we have removed the $shoulders slot but some users might still have it in their plans
          // set initial filter to recommended (which uses this slot)
          this.selectedCategoryId = 'recommended';
          this.filterBySlotId = this.planSubSlot.slotId;
          this.filterBySlotText = slot.slotName;
        }
      } else if (this.pageParams.initialSlotId) {
        // no plan subslot, but the caller knows which slot the choice belongs to (e.g. the
        // workout scanner pre-filtering by the AI-matched exercise's slot) — select that
        // slot's own category so the user can still switch to 'all' freely
        let slot = this.allSlots.find(s => s._id === this.pageParams.initialSlotId);
        if (slot && slot.exercises && slot.exercises.length > 0) {
          this.selectedCategoryId = slot._id;
          this.filterBySlotId = slot._id;
          this.filterBySlotText = slot.slotName;
        } else {
          this.selectedCategoryId = 'all';
          this.filterBySlotId = undefined;
        }
      } else {
        // No defined slot, show all exercises
        this.selectedCategoryId = 'all';
        this.filterBySlotId = undefined;
      }
      this.categorySelectionInitialized = true;
    }

    let now_local = this.debug.getNowLocal();
    let now_utc = now_local.toUTC();
    let fitnessState = await this.userDb.loadFitnessStateAsync(this.docWatcher);

    // collecting into temp objects and them setting at the end, to prevent conflicts
    let allExerciseUIOptions = [];
    let customUIOptions = [];

    // collect all exercises in the official database AND all customs up-front, so the
    // hide-forked-sources filter can consult both lists before either iteration runs.
    let allExercises = await this.exerciseDb.getAllExercisesAsync(this.docWatcher);
    let allCustomExercises = await this.userDb.getAllCustomExercisesAsync();

    // Build hidden-source set from un-filtered customs: every non-deleted UDE that is a
    // fork contributes its source id (isForkUde() excludes the import-self-reference).
    // forkBySourceId maps each forked source id → fork UDE. Used to substitute forks
    // for their (now-hidden) source in the recommendation result and the slot filter,
    // so a fork takes the source's place in the Recommended tab.
    const hiddenSourceIds = new Set<string>();
    this.forkBySourceId = new Map<string, CustomExercise>();
    for (const ude of allCustomExercises) {
      if (ude.virtuallyDeleted) continue;
      if (!isForkUde(ude)) continue;
      hiddenSourceIds.add(ude.local.sourceExerciseId);
      this.forkBySourceId.set(ude.local.sourceExerciseId, ude);
    }

    // Phase 2 of #3198 — coach version wins. If the user has a coach AND the coach has
    // forked the same source as the user's own UDE, hide the user's competing fork (the
    // coach's UDE is itself a fork already in `forkBySourceId`, so it occupies the
    // source-slot). The user's UDE doc remains in `userdb-*` — only filtered out of the
    // picker UI; Phase 3 surfaces it again on detach. Pure logic lives in
    // `services/coach-picker-hide` and is unit-tested there.
    const coachRelAsClient = this.userDb.loadCoachRelAsClient();
    const coachUid = coachRelAsClient?.coachUid;
    const coachAuthorIdentityHash = coachUid ? await computeAuthorIdentityHashAsync(coachUid) : undefined;
    const hiddenUserUdesByCoach = buildHiddenUserUdesByCoach(allCustomExercises, coachAuthorIdentityHash);

    allExercises = allExercises.filter(ex => !hiddenSourceIds.has(ex._id));
    allCustomExercises = allCustomExercises.filter(ex => !hiddenSourceIds.has(ex._id) && !hiddenUserUdesByCoach.has(ex._id));

    // Coach display name for the per-row "Coach: <name>" badge below. Read once per
    // picker render (cheap singleton getter); if the user has no coach, this is empty
    // and the badge never fires.
    const coachDisplayName = coachRelAsClient?.coachName ?? '';

    // Phase 3 of #3198 — resolve former-coach identity hashes (excluding the active
    // coach to handle the re-claim sync-lag window — see services/former-coach-resolver.ts).
    // Used below to stamp `isFromFormerCoach` + `formerCoachName` on matching customs.
    const { hashToCoachName: formerCoachByHash } = await resolveFormerCoachHashesAsync(
      coachRelAsClient?.formerCoaches,
      coachUid,
    );

    for (let exercise of allExercises) {
      let workout: Plan_Workout = undefined;
      for (let s in this.fitnessPlan.subSlots) {
        if (this.fitnessPlan.subSlots[s].exercise_id === exercise._id) {
          workout = this.fitnessPlan.workouts.find(w => w.subSlotIds.includes(s));
          break;
        }
      }
      let { state } = ExerciseStat.getExerciseState(exercise._id, now_utc, this.fitnessPlan, fitnessState);
      allExerciseUIOptions.push({ exercise: exercise, selected: false, stateStateDesc: describeExerciseState(state), workout: workout, lexical: prepareLexicalCandidate(exercise.name) });
    }

    // collect all custom exercises (allCustomExercises was loaded + filtered above)
    for (let exercise of allCustomExercises) {
      if (exercise.virtuallyDeleted) {
        continue;
      }
      let workout: Plan_Workout = undefined;
      for (let s in this.fitnessPlan.subSlots) {
        if (this.fitnessPlan.subSlots[s].exercise_id === exercise._id) {
          workout = this.fitnessPlan.workouts.find(w => w.subSlotIds.includes(s));
          break;
        }
      }
      // Compute state desc for customs the same way as globals, so forks (and any UDE
      // referenced by an active subslot) get the "Active" badge. Without this, customs
      // were missing the badge — pre-existing gap surfaced by the fork feature.
      let { state } = ExerciseStat.getExerciseState(exercise._id, now_utc, this.fitnessPlan, fitnessState);
      // Phase 2 of #3198 — flag coach-authored UDEs so the row gets a visible badge.
      // Pre-condition: coachAuthorIdentityHash !== undefined (no-coach short-circuit
      // matches the picker hide-policy contract in `coach-picker-hide.ts`).
      const isCoachAuthored = !!coachAuthorIdentityHash && exercise.authorIdentityHash === coachAuthorIdentityHash;
      // Phase 3: former-coach match by authorIdentityHash. Active-coach is excluded in
      // the resolver, so isCoachAuthored && isFromFormerCoach are mutually exclusive.
      const formerCoachName = !isCoachAuthored && exercise.authorIdentityHash
        ? formerCoachByHash.get(exercise.authorIdentityHash)
        : undefined;
      customUIOptions.push({
        exercise: exercise,
        selected: false,
        stateStateDesc: describeExerciseState(state),
        workout: workout,
        lexical: prepareLexicalCandidate(exercise.name),
        isCoachAuthored,
        coachDisplayName: isCoachAuthored ? coachDisplayName : undefined,
        isFromFormerCoach: !!formerCoachName,
        formerCoachName,
      });
    }

    this.allExerciseUIOptions = allExerciseUIOptions;
    this.customUIOptions = customUIOptions;

    // R3 — fire-and-forget: light the "update available" dot on imported forks whose
    // source has a genuine pending update. Off the critical render path.
    void this.detectUpdatesAvailableAsync(customUIOptions);

    // figure out which exercises are suggested for the current slot. When a suggested
    // global has a local fork, stamp suggestedIndex on the FORK's UI option (in customs)
    // so the fork appears in Recommended in the source's place — matches the picker's
    // hide-source semantics: the user sees only their version.
    if (this.planSubSlot) {
      const suggestedExercisesIds = await this.getReplacementExercises(this.exercise, this.planSubSlot);
      for (let i = 0; i < suggestedExercisesIds.length; i++) {
        const suggestedId = suggestedExercisesIds[i];
        const fork = this.forkBySourceId.get(suggestedId);
        if (fork) {
          const customUIOption = customUIOptions.find(e => e.exercise._id === fork._id);
          if (customUIOption) customUIOption.suggestedIndex = i;
        } else {
          const suggestedUIOption = allExerciseUIOptions.find(e => e.exercise._id === suggestedId);
          if (suggestedUIOption) suggestedUIOption.suggestedIndex = i;
        }
      }
    }

    // this will fill the displayed list
    await this.searchAsync(this.searchText);

    // do the initial selection - either the exercise that was passed in, or the newly created/edited one when returning from subpage
    let initiallySelectedId = this.selectExerciseByIdWhenLoading || (this.exercise && this.exercise._id);
    if (initiallySelectedId) {
      let initialSelection = this.displayedUIOptions.find(e => e.exercise && e.exercise._id === initiallySelectedId);
      if (initialSelection) {
        this.setSelectedItem(initialSelection);
        this.selectedItemId = initiallySelectedId;
      }
    }
    this.selectExerciseByIdWhenLoading = undefined;
    
    // Hide spinner and update view after initial selection is complete
    this.showSpinner = false;
    this.isInitializing = false; // Allow interaction now that initial selection is set
    this.cd.markForCheck();

    // Phase 3 §6 — one-time post-detach notice. Per-entry flag in `formerCoaches[]`
    // (NOT localStorage — survives multi-device and account boundaries). Single write
    // site lives on the picker entry path to avoid double-toasts on fast navigation.
    void this.maybeShowFormerCoachNoticeAsync();
  }

  /**
   * Phase 3 §6: show a one-shot toast on the first picker entry after detach so
   * the user knows their plan still uses the former coach's exercises and can
   * switch back to their own version. Persisted by setting `noticeShown = true`
   * on every `formerCoaches[]` entry that didn't already have it. Gated on
   * `coachUid` empty so an active-coaching session never sees it.
   */
  private async maybeShowFormerCoachNoticeAsync(): Promise<void> {
    if (this.viewDestroyed) return;
    const persisted = await this.markAndPersistFormerCoachNoticeAsync();
    if (!persisted || this.viewDestroyed) return;
    this.toastController.showToast({
      message: LL.coach.detachExerciseNotice(),
      type: ToastType.SuccessLight,
      duration: 5000,
    });
  }

  /**
   * Marks every `formerCoaches[]` entry's `noticeShown = true`, persists, and
   * verifies the flag actually landed. Returns true iff persistence is
   * confirmed (so the caller can safely show the toast without risking a
   * re-fire on the next picker entry).
   *
   * Why post-save verify: `saveRecordInternalAsync` catches 409 conflicts and
   * runs `mergeConflictingDocs` internally, but the merge uses "longer-array
   * wins" semantics on arrays (shared/src/deep-helpers.ts). If a concurrent
   * server-side detach appended a NEW entry to `formerCoaches` between our
   * picker's read and our save, the server array is longer → merge picks the
   * server array wholesale → our per-entry `noticeShown = true` flips are
   * silently dropped. The save returns success but the flag never persists.
   * Detect this by re-reading and, if any entry is still unflagged, re-marking
   * and re-saving once. If the second save still doesn't take, give up (toast
   * will re-fire next time but that's acceptable — extreme racy case).
   */
  private async markAndPersistFormerCoachNoticeAsync(): Promise<boolean> {
    const tryOnce = async (): Promise<{ saved: boolean; allMarked: boolean }> => {
      const rel = this.userDb.loadCoachRelAsClient();
      if (!rel || rel.coachUid) return { saved: false, allMarked: true };
      const formerCoaches = rel.formerCoaches;
      if (!formerCoaches?.some(e => !e.noticeShown)) return { saved: false, allMarked: true };
      for (const entry of formerCoaches) {
        if (!entry.noticeShown) entry.noticeShown = true;
      }
      try {
        await this.userDb.saveCoachRelAsClientAsync(DatabaseWrapper.getTransactionId());
      } catch (err: any) {
        console.error('exercise-switch: failed to persist former-coach notice flag', err);
        this.logging.trackSilentProblem('exercise-switch: failed to persist former-coach notice flag', { err: String(err?.message ?? err) });
        return { saved: false, allMarked: false };
      }
      // Post-save verify against the singleton (which now reflects any
      // mergeConflictingDocs outcome from a concurrent server doc).
      const after = this.userDb.loadCoachRelAsClient();
      const stillUnmarked = !!after?.formerCoaches?.some(e => !e.noticeShown);
      return { saved: true, allMarked: !stillUnmarked };
    };

    const first = await tryOnce();
    if (first.allMarked) return first.saved;
    // First attempt persisted-but-merge-dropped or never saved. Try once more
    // against the now-merged singleton.
    const second = await tryOnce();
    if (!second.allMarked) {
      this.logging.trackSilentProblem('exercise-switch: noticeShown flag did not persist after verify-retry', {});
      return false;
    }
    return second.saved;
  }

  // this takes all the possible ui items and filters them according to the current filters,
  // making the list that will be searched
  applyFilters() {

  }

  async ionViewWillLeave() {

  }

  async ionViewDidLeave() {
  }

  async getReplacementExercises(exercise: Exercise, planSubSlot: Plan_SubSlot): Promise<string[]> {
    let slot = await this.exerciseDb.slotForIdAsync(planSubSlot.slotId, this.docWatcher);
    // if slot doesn't exist in DB (e.g. synthetic slot/$custom for custom exercises), no suggestions
    if (!slot) return [];
    let fitnessState = await this.userDb.loadFitnessStateAsync(this.docWatcher);
    let utc_now = this.debug.getNowLocal().toUTC();
    // List all exercises in current workout
    let exerciseToAvoid: string[] = [];
    for (let subslot of this.workout.subSlotIds) {
      // skip the targeted subslot itself (we don't avoid our own exercise). Skip by
      // subSlotId, not by `exerciseId === this.exercise._id` — those can diverge
      // after a fork-delete patches the targeted subslot to the ancestor while
      // this.exercise remains the (virtually-deleted) fork as the page target;
      // the exerciseId-match check would then put the ancestor on the avoid list
      // and the recommendation engine would push it to the bottom of Recommended.
      if (subslot === this.pageParams.subSlotId) continue;
      let exerciseId = this.fitnessPlan.subSlots[subslot]?.exercise_id;
      if (exerciseId) exerciseToAvoid.push(exerciseId);
    }
    // When the page anchor is a fork, treat its source as the anchor for the
    // suggestion engine. Otherwise the fork (UDE) takes the isCustomExerciseById
    // branch in suggestAvailableExercisesAsync, which skips the `concat([anchorId],
    // exercises)` prefix that would put a global anchor at index 0. The source
    // ends up at its natural slot.exercises index instead — and the picker's
    // fork-substitute loop stamps that index on the fork, so the fork's row jumps
    // around between sessions depending on where its source sits in the catalog.
    // Anchoring on the source keeps the fork at the same Recommended-tab position
    // the source would have held if it were still the page target.
    const ude = exercise as CustomExercise;
    const anchorId = isForkUde(ude) ? ude.local.sourceExerciseId : exercise._id;
    return suggestAvailableExercisesAsync(
      this.exerciseDb,
      fitnessState,
      this.fitnessPlan,
      slot,
      planSubSlot.startIndex,
      anchorId,
      utc_now,
      exerciseToAvoid,
      this.docWatcher,
    );
  }

  handleStatsAndNotesButtonClick(option: ExerciseSwitchUIOption) {
    this.telemetry.trackEvent('exercise_switch_stats_and_notes', { exercise: option.exercise._id });
    void this.navCtrl.pushAsync(IExerciseStatsPage, { id: option.exercise._id });
  }

  handleShareExerciseFromList(option: ExerciseSwitchUIOption) {
    // navigate to exercise-stats page where the share flow lives
    void this.navCtrl.pushAsync(IExerciseStatsPage, { id: option.exercise._id });
  }

  handleDescriptionButtonClick(option: ExerciseSwitchUIOption) {
    this.telemetry.trackEvent('exercise_switch_exercise_description', { exercise: option.exercise._id });
    void this.navCtrl.pushAsync(IHelpCardPage, {
      helpId: '@instructions:' + option.exercise._id,
    });
  }

  getSelectedItem(): ExerciseSwitchUIOption {
    return this.displayedUIOptions.find(e => e.selected);
  }

  setSelectedItem(option: ExerciseSwitchUIOption) {
    // Prevent duplicate selection by clearing all selections first
    for (let e of this.displayedUIOptions) {
      e.selected = false;
    }
    if (option) {
      option.selected = true;
      // if no selection passed it, select the first one
    } else {
      if (this.displayedUIOptions.length > 0) {
        this.displayedUIOptions[0].selected = true;
      }
    }
    // Trigger change detection to update the view immediately
    this.cd.markForCheck();
  }

  selectionChanged(): boolean {
    const selectedItem = this.getSelectedItem();
    if (selectedItem && selectedItem.exercise) {
      let originalExerciseId = this.exercise && this.exercise._id;
      return selectedItem.exercise._id !== originalExerciseId;
    }

    return false;
  }

  ButtonType = ButtonType;

  handleAcceptButtonClicked(event: Event, _buttonType: ButtonType) {
    lockClick(event, this.handleAcceptButton());
  }

  handleCancelButtonClicked(event: Event, _buttonType: ButtonType) {
    this.telemetry.trackEvent('exercise_switch_response', { accepted: false });
    lockClick(event, this.navCtrl.popAsync());
  }

  private isAccepting = false;

  async handleAcceptButton() {
    if (this.isAccepting) return;
    this.isAccepting = true;
    // reset the re-entrancy guard on EVERY exit (incl. thrown/early-return paths) so a
    // failure can never permanently brick the Add button — the silent dead-button half
    // of #3483. Any unexpected error surfaces (Sentry + user toast) instead of being
    // swallowed by lockClick's catch-less .finally.
    try {
      await this.acceptSelectionAsync();
    } catch (err) {
      this.logging.trackAbnormalException(err, LL.errors.userFacing.updateWorkoutFailed());
    } finally {
      this.isAccepting = false;
    }
  }

  // perform the accept: validate the selection, then either add a new exercise
  // (clone + navigate to config), prompt temp-vs-permanent swap, or pop with the
  // chosen exercise. may early-return on missing selection or dismissed dialog.
  private async acceptSelectionAsync() {
    let selectedItem = this.getSelectedItem();
    if (!selectedItem) {
      this.logging.trackAbnormalError(LL.exerciseSwitch.errors.noSelectedItem());
      return;
    }

    // the accept is recorded only once every question below has been answered, so a pick the
    // user backs out of is not counted as accepted (and a second try is not counted twice)

    // if adding a new exercise, create it on a clone and navigate to config page
    // so the user can edit sets/rep targets before the exercise is actually added
    const returnContext = this.navParams.data.local?.returnContext as {
      action: string, workoutName: string, workoutReference: number, insertAtIndex?: number,
    };
    if (returnContext?.action === 'add') {
      // if the add would link this workout to another one, the user may still back out
      if (!await this.confirmLinkingWorkoutsAsync(selectedItem)) {
        return;
      }
      this.trackSwitchAccepted(selectedItem);
      const fitnessPlanClone = deepClone(this.fitnessPlan);
      const fitnessStateClone = deepClone(this.fitnessState);

      const { newSubslotId } = await addExerciseToWorkoutAsync(
        returnContext.workoutReference,
        selectedItem.exercise._id,
        fitnessPlanClone, fitnessStateClone,
        this.exerciseDb, this.userDb, this.debug.getNowLocal(), this.docWatcher
      );

      // if adding at a specific position, move from end to correct index
      if (returnContext.insertAtIndex !== undefined && newSubslotId) {
        const workout = fitnessPlanClone.workouts[returnContext.workoutReference];
        const currentIndex = workout.subSlotIds.indexOf(newSubslotId);
        if (currentIndex >= 0 && currentIndex !== returnContext.insertAtIndex) {
          workout.subSlotIds.splice(currentIndex, 1);
          workout.subSlotIds.splice(returnContext.insertAtIndex, 0, newSubslotId);
        }
      }

      // replace self with config page — one smooth animation, no gap
      await this.navCtrl.replaceTopAsync(IExerciseConfigurationPage, {
        subSlotId: newSubslotId,
        returnCallback: 'onExerciseAddConfigResult',
        local: {
          fitnessPlan: fitnessPlanClone,
          fitnessState: fitnessStateClone,
          // carry the workout NAME (stable) alongside the index — onExerciseAddConfigResult
          // resolves the target workout by name so it can't drift if the page reloads on
          // return from the picker (root of the orphaned add, Sentry Group 8 / 44C).
          returnContext: { newSubslotId, workoutName: returnContext.workoutName, workoutReference: returnContext.workoutReference, insertAtIndex: returnContext.insertAtIndex },
        },
      });
      return;
    }

    // if temp swap prompt is enabled (active workout context), ask before returning
    if (this.pageParams.showTempSwapPrompt) {
      const isTemporary = await this.alertCtrl.showAlertAsync<boolean>({
        title: LL.workouts.alerts.switchType(),
        message: LL.workouts.alerts.switchTypeMessage(),
        buttons: [
          { text: LL.workouts.alerts.permanently(), role: AlertButtonRole.Primary, result: false },
          { text: LL.workouts.alerts.thisSessionOnly(), role: AlertButtonRole.Cancel, result: true },
        ],
      });

      // if X button dismissed, abort — stay on this page (the finally in
      // handleAcceptButton resets isAccepting)
      if (isTemporary === undefined) {
        return;
      }

      // a session-only switch leaves the plan alone, so only a permanent one can list an
      // exercise twice in this workout or link workouts
      if (!isTemporary && await this.refuseExerciseAlreadyInThisWorkoutAsync(selectedItem)) {
        return;
      }
      if (!isTemporary && !await this.confirmLinkingWorkoutsAsync(selectedItem)) {
        return;
      }

      this.trackSwitchAccepted(selectedItem);
      this.telemetry.trackEvent('exercise_switch_duration', {
        type: isTemporary ? 'temporary' : 'permanent',
        switched_exercise: this.exercise && this.exercise._id,
        replacement_exercise: selectedItem.exercise._id,
      });

      await this.navCtrl.popAsync({ exerciseId: selectedItem.exercise._id, isTemporary });
      return;
    }

    // change mode — refuse an exercise this workout already has (the picker stays open), then
    // pop with exercise ID as before, unless the user backs out of linking workouts
    if (await this.refuseExerciseAlreadyInThisWorkoutAsync(selectedItem)) {
      return;
    }
    if (!await this.confirmLinkingWorkoutsAsync(selectedItem)) {
      return;
    }
    this.trackSwitchAccepted(selectedItem);
    await this.navCtrl.popAsync(selectedItem.exercise._id);
  }

  /** Record that the user accepted this pick. Called only after every question the accept asks
   *  has been answered, so a pick the user backs out of is never counted. */
  private trackSwitchAccepted(selectedItem: ExerciseSwitchUIOption) {
    this.telemetry.trackEvent('exercise_switch_response', {
      accepted: true,
      switched_exercise: this.exercise && this.exercise._id,
      replacement_exercise: selectedItem.exercise._id,
    });
  }

  /**
   * Ask before a pick that would make this workout share one progression with another workout.
   *
   * Adding or switching to an exercise the plan already holds reuses that exercise's subslot, so
   * every workout listing it trains one shared progression, and a later swap or set change in one
   * also changes the others. That used to happen without a word (#4004). Returns false when the
   * user backs out, and the caller then stays on the picker. Only callers that pass
   * `warnBeforeLinkingWorkouts` get the question. See design-docs/exercise-replacement.md.
   */
  private async confirmLinkingWorkoutsAsync(selectedItem: ExerciseSwitchUIOption): Promise<boolean> {
    if (!this.pageParams.warnBeforeLinkingWorkouts) {
      return true;
    }
    // keeping the exercise this subslot already holds links nothing new
    if (this.planSubSlot?.exercise_id === selectedItem.exercise._id) {
      return true;
    }
    const otherWorkouts = otherWorkoutsSharingExercise(this.fitnessPlan, selectedItem.exercise._id, this.workout.name);
    if (otherWorkouts.length === 0) {
      return true;
    }
    const share = await this.alertCtrl.showAlertAsync<boolean>({
      title: LL.exerciseSwitch.linkWorkouts.title(),
      message: LL.exerciseSwitch.linkWorkouts.message({
        exercise: selectedItem.exercise.name,
        workouts: otherWorkouts.map(w => LL.exerciseSwitch.linkWorkouts.workoutName({ workout: w.name })).join(', '),
      }),
      buttons: [
        { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel, result: false },
        { text: LL.exerciseSwitch.linkWorkouts.share(), role: AlertButtonRole.Primary, result: true },
      ],
    });
    // closing the alert with its X answers undefined, which backs out like Cancel
    return share === true;
  }

  /**
   * Refuse changing an exercise into one this workout already lists. The change would reuse that
   * exercise's subslot and list it twice; the save dropped the copy, so the workout silently lost
   * the exercise being changed (#6296). Returns true when the pick was refused; the caller then
   * stays on the picker. `changeExerciseInWorkoutAsync` refuses the same case as a last resort.
   */
  private async refuseExerciseAlreadyInThisWorkoutAsync(selectedItem: ExerciseSwitchUIOption): Promise<boolean> {
    // only a change of an exercise in a known workout can list one twice
    if (!this.pageParams.exerciseId || !this.workout || !this.fitnessPlan) {
      return false;
    }
    // keeping the exercise this subslot already holds changes nothing
    if (this.planSubSlot?.exercise_id === selectedItem.exercise._id) {
      return false;
    }
    // the same check the switch makes, so the two cannot disagree
    if (!workoutAlreadyListsExercise(this.fitnessPlan, selectedItem.exercise._id, this.workout, this.pageParams.subSlotId)) {
      return false;
    }
    await this.alertCtrl.showAlertAsync({
      title: LL.exerciseSwitch.alreadyInWorkout.title(),
      message: LL.exerciseSwitch.alreadyInWorkout.message({ exercise: selectedItem.exercise.name, workout: this.workout.name }),
      buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
    });
    return true;
  }

  getPageTitle(): TranslatedString {
    // if switching an exercise in a workout
    if (this.pageParams.exerciseId) {
      return LL.exerciseSwitch.titles.changeExercise();
      // if adding a new exercise to an existing workout
    } else if (!this.pageParams.isNewWorkout) {
      return LL.exerciseSwitch.titles.addExercise();
      // if creating a new workout
    } else {
      return LL.exerciseSwitch.titles.pickExercise();
    }
  }

  isExerciseUsedInSameWorkout(uie: ExerciseSwitchUIOption) {
    return uie.selected && uie.workout && this.workout.name === uie.workout.name;
  }

  isExerciseUsedInOtherWorkout(uie: ExerciseSwitchUIOption) {
    return uie.selected && uie.workout && this.workout.name !== uie.workout.name;
  }

  handleCreateExerciseButtonClick() {
    void this.navCtrl.pushAsync(IEditExercisePage, {
      id: '',
      mode: 'custom',
      returnCallback: 'onExerciseCreated',
      local: {
        returnContext: { slotId: this.filterBySlotId },
      },
    });
  }

  async onExerciseCreated(context: { slotId: string }, newExerciseId: string) {
    if (newExerciseId && newExerciseId !== '') {
      let customExercise: CustomExercise;
      try {
        customExercise = await this.userDb.getCustomExerciseByIdAsync(newExerciseId);
      } catch (err) {
        // if the editor handed back an id that was never saved, nothing was created. The
        // editor no longer does that (#6267); report it under its own name if it comes back,
        // instead of letting the rejection escape unowned into the shared not_found bucket
        if (!isPouchNotFound(err)) throw err;
        this.logging.trackSilentProblem('CreatedExerciseNotFound', { exerciseId: newExerciseId });
        return;
      }
      // if a slot filter is active, assign the exercise to that group
      if (context.slotId) {
        customExercise.usedInSlots.push(context.slotId);
      }
      await this.userDb.saveCustomExerciseAsync(customExercise, DatabaseWrapper.getTransactionId());
      this.selectExerciseByIdWhenLoading = newExerciseId;
      this.cd.markForCheck();
    }
  }

  handleModifyExerciseButtonClick(uie: ExerciseSwitchUIOption) {
    this.wasSelectedBeforeEdit = this.selectedItemId === uie.exercise._id;
    void this.navCtrl.pushAsync(IEditExercisePage, {
      id: uie.exercise._id,
      returnCallback: 'onExerciseModified',
    });
  }

  // R3 — "Update" / "Check for Update" menu handler. Opens the diff picker IN PLACE
  // (app-level modal) instead of navigating to edit-exercise-page, then applies at the
  // canonical-doc level (no draft — the exercise isn't open for editing). The whole sequence
  // lives in `CoachForkUpdateFlow`, shared with exercise-stats; what is page-specific is the
  // row refresh below.
  async handleCheckForUpdate(uie: ExerciseSwitchUIOption) {
    await this.forkUpdateFlow.checkForUpdateAsync({
      fork: uie.exercise as CustomExercise,
      onApplied: canonical => {
        // refresh the row: the saved canonical is the identity-map object; point the row at
        // it and clear the "update available" dot (the rev is now seen / applied). The menu
        // item stays available as "Check for Update" (a partial apply / ignore is resolved).
        uie.exercise = canonical;
        uie.hasUpdateAvailable = false;
        this.cd.markForCheck();
      },
    });
  }

  // returnCallback from the ExerciseDiffPage modal. This name has to stay a method on the
  // page: `NavController` finds the handler by walking the nav stack for a page instance
  // carrying it. The body is shared — see `CoachForkUpdateFlow.handlePickerResultAsync`.
  async onDiffPickerResult(_context: unknown, result?: ExerciseDiffResult): Promise<void> {
    await this.forkUpdateFlow.handlePickerResultAsync(result);
  }

  // Template guard: render the badges row only when at least one badge is present, so an
  // empty flex row doesn't add a stray gap under the name. (A partial apply / ignore is
  // treated as resolved — there is no "update pending" badge; the per-row dot, driven by
  // `hasUpdateAvailable`, is the only update affordance and lives next to the name.)
  hasAnyBadge(item: ExerciseSwitchUIOption): boolean {
    // Test the STATE key, never the title -- the title is translated text, so comparing it to
    // an English literal would show the 'unused' badge to every non-English user (#4368).
    const hasStateBadge = !!item.stateStateDesc && item.stateStateDesc.state !== 'unused';
    return hasStateBadge || !!item.isCoachAuthored || !!item.isFromFormerCoach;
  }

  // Phase 3 — "Former coach" badge label + tooltip. Mobile tap and desktop hover are
  // both covered by `cs-tooltip` (it opens a bottom-sheet popover on touch).
  protected formerCoachBadgeLabel(): TranslatedString {
    return LL.coach.formerCoachBadge.label();
  }

  protected formerCoachBadgeTooltip(item: ExerciseSwitchUIOption): TranslatedString {
    const name = item.formerCoachName?.trim();
    if (!name) {
      // Contract violation: resolver only sets isFromFormerCoach=true when a
      // name was matched, so a missing name implies the formerCoaches[] entry
      // arrived without coachName (server-side helper defaults to
      // DEFAULT_COACH_NAME_FALLBACK, so this means data corruption upstream).
      // Surface and substitute.
      this.logging.trackSilentProblem('exercise-switch: former-coach badge missing coachName', { docId: item.exercise?._id });
      return LL.coach.formerCoachBadge.tooltip({ coachName: DEFAULT_COACH_NAME_FALLBACK });
    }
    return LL.coach.formerCoachBadge.tooltip({ coachName: name });
  }

  // Debounce coalescing rapid live-refresh signals (a coach edit can apply several docs
  // in quick succession) into a single re-detection pass.
  private redetectScheduled = false;

  // R3 — schedule a debounced re-run of the per-row update detection. Triggered from the
  // `coachExerciseApplied$` subscription when the apply pipeline lands a coach doc (a
  // same-device local write that bypasses the cross-device docWatcher), so the "update
  // available" dot / menu update live without a reload.
  private scheduleUpdateAvailableRedetect(): void {
    if (this.redetectScheduled) return;
    this.redetectScheduled = true;
    this.safeTimeout(() => {
      this.redetectScheduled = false;
      if (this.viewDestroyed) return;
      void this.detectUpdatesAvailableAsync(this.customUIOptions);
    }, 500);
  }

  // R3 — fire-and-forget per-row "update available" detection. For each imported custom
  // fork (has `local.sourceExerciseId`, NOT a coach copy), check whether a genuinely-new
  // source rev exists and stamp `hasUpdateAvailable` → drives the dot next to the name.
  // `checkImportedExerciseForUpdateAsync` reads the local `global_exercises` staging DB
  // first (cheap for coach forks; cloud fallback only for public-share imports), and its
  // `lastSeenSourceRevision` guard means an already-seen rev (ignored / partially-applied)
  // does NOT light the dot — the fork is treated as resolved, and the ⋮ menu still offers
  // the always-available "Check for Update". Runs sequentially to avoid a fetch storm.
  private async detectUpdatesAvailableAsync(options: ExerciseSwitchUIOption[]): Promise<void> {
    // throwaway watcher: detection reads source docs only to compare revs — registering
    // those ids on the long-lived page `docWatcher` would grow its registry on every
    // redetect. The live re-detect trigger is the `coachExerciseApplied$` subscription,
    // not doc-watch registrations.
    await this.exerciseDb.database.withDocWatcherAsync(async watcher => {
      for (const option of options) {
        if (this.viewDestroyed) return;
        const ex = option.exercise;
        if (!isCustomExercise(ex)) continue;
        if (!ex.local?.sourceExerciseId || ex.local?.coachShared) continue;
        try {
          const result = await this.exerciseDb.checkImportedExerciseForUpdateAsync(ex, watcher, this.resolveLocalSource);
          if (this.viewDestroyed) return;
          // assign the boolean (not just set-true) so a live re-detection CLEARS a stale
          // dot once the update is applied / no longer available.
          if (option.hasUpdateAvailable !== result.hasUpdate) {
            option.hasUpdateAvailable = result.hasUpdate;
            this.cd.markForCheck();
          }
        } catch (err: any) {
          // non-fatal per-row: a failed check just leaves the dot off. Surface so a
          // systemic failure (e.g. staging DB closed) isn't invisible.
          console.error('exercise-switch: update-available check failed', err);
          this.logging.trackSilentProblem('exercise-switch: update-available check failed', {
            docId: ex._id, err: String(err?.message ?? err),
          });
        }
      }
    });
  }

  // Fork-on-edit entry point for globals: opens edit-exercise-page in Mode.ForkOnEdit.
  // Save-time fork branch is responsible for navigation (replaceTopAsync to the new
  // fork). returnCallback fires when the user pops back from the new fork's edit page —
  // popAsync passes the fork's _id, so onExerciseModified sets selectExerciseByIdWhenLoading
  // and the picker auto-selects the fork on rebuild.
  handleForkOnEditButtonClick(uie: ExerciseSwitchUIOption) {
    this.wasSelectedBeforeEdit = this.selectedItemId === uie.exercise._id;
    void this.navCtrl.pushAsync(IEditExercisePage, {
      id: uie.exercise._id,
      mode: 'forkOnEdit',
      returnCallback: 'onExerciseModified',
    });
  }

  onExerciseModified(_context: unknown, newExerciseId: string) {
    // Reset the pre-edit-selection flag first, before any early return: cancel-back from
    // edit-exercise also lands here (with empty `newExerciseId`), and the flag must clear
    // so a stale `true` can't survive into a future return-callback path that consumes
    // it without re-writing it. Today every Modify / Fork-on-Edit entry point re-writes
    // the flag, so a stale value is masked — this reset is defensive against future
    // call sites being added.
    const wasSelected = this.wasSelectedBeforeEdit;
    this.wasSelectedBeforeEdit = false;
    // if page was closed without saving (e.g. header back button), ignore
    if (!newExerciseId) return;
    // Only re-select on return if the edited row was the user's selection BEFORE they
    // opened edit-exercise. Opening Modify on an unselected row to view / tweak then
    // returning should not suddenly mark it as the picker's selection. Fork-on-edit
    // preserves the source's selected state by carrying it onto the new fork id.
    if (wasSelected) {
      this.selectExerciseByIdWhenLoading = newExerciseId;
    }
    // fork-on-edit: edit page returns the new fork's id when the source was forked.
    // Patch the local plan snapshot so the picker's Active-badge logic recognizes
    // the fork (planSubSlot.exercise_id is otherwise the now-superseded source).
    if (this.fitnessPlan && this.pageParams.subSlotId) {
      const subSlot = this.fitnessPlan.subSlots[this.pageParams.subSlotId];
      if (subSlot && subSlot.exercise_id !== newExerciseId) {
        // navParams snapshot patch (not a plan mutation): the live plan in userDb was
        // already updated by edit-exercise-page's fork-on-save path. This only refreshes
        // the picker's stale local copy so Active-badge logic sees the fork.
        // eslint-disable-next-line no-restricted-syntax
        subSlot.exercise_id = newExerciseId;
      }
    }
  }

  async handleDeleteExerciseButtonClick(uie: ExerciseSwitchUIOption) {
    // only custom exercises can be deleted
    if (!isCustomExercise(uie.exercise)) {
      return;
    }

    // different message depending on whether the exercise was used
    let extraText = this.customExerciseIsUnused(uie.exercise._id) ?
      LL.exerciseSwitch.alerts.deleteExercise.neverUsed() :
      LL.exerciseSwitch.alerts.deleteExercise.wasUsed();

    let doit = await this.alertCtrl.showAlertAsync({
      title: LL.exerciseSwitch.alerts.deleteExercise.title(),
      message: LL.exerciseSwitch.alerts.deleteExercise.message({ name: uie.exercise.name, extra: extraText }),
      buttons: [
        { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel, result: false },
        { text: LL.common.buttons.delete(), role: AlertButtonRole.Primary, result: true, },
      ]
    });
    if (!doit) {
      return;
    }


    const transactionId = DatabaseWrapper.getTransactionId();
    const ude = uie.exercise as CustomExercise;

    // mark it as deleted
    uie.exercise.virtuallyDeleted = true;
    await this.userDb.saveCustomExerciseAsync(uie.exercise, transactionId);

    // if it was never used
    // Forks ALWAYS survive as virtuallyDeleted so the Revert chain-walk can climb
    // through them. Hard-deleting a fork would break any descendant fork's chain.
    if (!isForkUde(ude) && this.customExerciseIsUnused(uie.exercise._id)) {
      // delete it completely (we can't do this to those that were used or otherwise viewing old WOs will break)
      //
      // nobody awaits this delete, so a rejection here would become an unhandled rejection.
      // Since df-6ce1f9d6830e the delete THROWS when the database is being torn down rather
      // than returning as though it had removed the document, and that is our own shutdown:
      // the exercise is already marked `virtuallyDeleted` above, which is the state the app
      // reads, so the hard delete simply not happening costs nothing. Anything else is a real
      // failure and is reported.
      void this.userDb.deleteCustomExerciseAsync(uie.exercise as CustomExercise, transactionId).catch(err => {
        if (isDatabaseTornDown(err)) return;
        this.logging.trackSilentProblem('exercise-switch: hard-deleting an unused custom exercise failed', {
          docId: ude._id,
          error: describeErrorForLog(err),
        });
      });
    }

    // Phase 2 of #3198 — when a coach with claimed clients deletes a UDE that may have
    // auto-published to the coach channel, retract it. Server logic handles the dual-flag
    // case: if the doc is also publicly shared, only `local.coachShared` clears (doc
    // survives); otherwise the doc is deleted entirely. Fire-and-forget; if no doc exists
    // server-side, the call is idempotent.
    void this.maybeUnpublishFromCoachChannelAsync(ude);

    // If a fork is being deleted AND it is the current subSlot's exercise, walk
    // the lineage chain to the nearest live ancestor and rewrite the plan back
    // to it (mirrors Revert's semantic — deleting an active fork restores the
    // user's original exercise). Otherwise the plan keeps pointing at the now
    // virtually-deleted fork; the workout-loader's recovery path would then call
    // replaceExerciseInSubSlot on next workout load and silently wipe progression.
    let restoredAncestorId: string | undefined = undefined;
    if (isForkUde(ude)) {
      // The chain walk reads both databases and lets an unavailable one travel out. It must
      // not travel HERE: the fork was already marked deleted and saved above, this method is
      // started with `void`, and a throw would abort before the plan rewrite and the picker
      // rebuild below — a half-applied delete the user is told nothing about. Treat "could
      // not read" as "no ancestor found", which is a state this code already handles: the
      // plan keeps pointing at the virtually-deleted fork and the workout loader's recovery
      // path picks it up, exactly as when the chain genuinely has no live ancestor. Report it
      // as the expected transient condition it is rather than letting it pass unrecorded.
      try {
        restoredAncestorId = await findNearestLiveAncestorAsync(ude.local?.sourceExerciseId, this.userDb, this.exerciseDb, this.docWatcher);
      } catch (e) {
        // the walk's curated read throws every failure but a not-found (df-c21e11979ef2), so a
        // read that merely failed arrives here too. It gets the same "no ancestor found"
        // handling for the same reason: a throw would leave the delete half-applied.
        if (isDatabaseUnavailable(e)) {
          this.logging.trackSilentProblem('exercise-switch: ancestor walk skipped, database unavailable', {
            docId: ude._id,
          });
        } else {
          this.logging.trackSilentProblem('exercise-switch: ancestor walk skipped, read failed', {
            docId: ude._id, error: describeErrorForLog(e),
          });
        }
      }
      if (restoredAncestorId) {
        // mutate the LIVE plan singleton (this is what gets persisted by savePlanAsync
        // and what training/workout-detail read). Iterate ALL matching subSlots — same
        // rename pattern as edit-exercise's rewritePlanExerciseIdAsync.
        const livePlan = this.userDb.fitnessPlan;
        let didRewrite = false;
        if (livePlan?.subSlots) {
          for (const liveSubSlot of Object.values(livePlan.subSlots)) {
            if (liveSubSlot.exercise_id === ude._id) {
              // eslint-disable-next-line no-restricted-syntax -- fork delete = rename to ancestor; preserves periodization state
              liveSubSlot.exercise_id = restoredAncestorId;
              didRewrite = true;
            }
          }
        }
        // also patch picker's local navParams snapshot so post-rebuild UI sees the
        // rewrite (snapshot is a separate object from the singleton in live mode).
        if (this.fitnessPlan?.subSlots) {
          for (const snapSubSlot of Object.values(this.fitnessPlan.subSlots)) {
            if (snapSubSlot.exercise_id === ude._id) {
              // eslint-disable-next-line no-restricted-syntax -- snapshot patch, not a plan mutation
              snapSubSlot.exercise_id = restoredAncestorId;
            }
          }
        }
        if (didRewrite) await this.userDb.savePlanAsync(transactionId);
      }
    }

    // Rebuild picker from current data so source reappears (hiddenSourceIds is
    // rebuilt) and the ancestor gets the Active badge + radio selection. Setting
    // selectExerciseByIdWhenLoading drives the auto-select in ionViewDidEnter.
    // Note: pageParams.exerciseId is intentionally NOT retargeted — keeping it as
    // the (virtually-deleted) fork lets `suggestAvailableExercisesAsync` keep the
    // ancestor in its suggestion list at the original index, so the ancestor lands
    // in the same Recommended-tab position the fork held. Retargeting would make
    // the engine treat the ancestor as the page exercise → exclude it from its own
    // suggestions → no suggestedIndex → falls to the bottom.
    if (restoredAncestorId) {
      this.selectExerciseByIdWhenLoading = restoredAncestorId;
    }
    await this.ionViewDidEnter();
    this.cd.markForCheck();
  }

  private customExerciseIsUnused(customExerciseId: string) {
    return !this.fitnessState.exerciseStats[customExerciseId] || Object.keys(this.fitnessState.exerciseStats[customExerciseId]).length === 0;
  }

  // Phase 2 of #3198: retract an UDE from the coach channel after the coach deletes it.
  // Same gating as the auto-publish trigger — claimed clients exist, current user
  // owns the UDE. Server is idempotent on already-absent docs, and the dual-flag case
  // (doc also publicly shared) is handled server-side.
  private async maybeUnpublishFromCoachChannelAsync(exercise: CustomExercise): Promise<void> {
    // A background task must not reject: a rejected `void`-ed promise becomes an
    // unhandled rejection. `hasClaimedClientsAsync` reads the database and now
    // THROWS when it is unavailable (deferred #2) rather than answering with an
    // empty list, and the honest answer to "does this user have claimed clients"
    // is then "unknown" — so skip the channel work rather than guess either way.
    // It resumes on the next edit once the database is back.
    let hasClaimedClients: boolean;
    try {
      hasClaimedClients = await this.userDb.hasClaimedClientsAsync();
    } catch (err) {
      if (!isDatabaseUnavailable(err)) throw err;
      return;
    }
    if (!hasClaimedClients) return;
    // narrow try to the cloud call — config + claimed-clients reads above shouldn't fail.
    // The cloud function returns `{ error: '...' }` as a 200-OK body — server-side rejections
    // land on `result.error`, not on `err`.
    try {
      const result = await this.cloudFunctionsService.unpublishExerciseFromCoachChannelAsync({ exercise });
      if (result?.error) {
        console.error('coach-channel auto-unpublish rejected by server', result.error);
        this.logging.trackSilentProblem('coach-channel auto-unpublish rejected by server', { docId: exercise._id, serverError: result.error });
      }
    } catch (err: any) {
      // Reached only on network / runtime errors after the resilient-retry chain. Surface
      // — a persistent failure means the coach's deletion never reaches clients, which the
      // user has no other signal for.
      console.error('coach-channel auto-unpublish failed', err);
      this.logging.trackSilentProblem('coach-channel auto-unpublish failed', { docId: exercise._id, err: String(err?.message ?? err), stack: err?.stack });
    }
  }

  ngOnDestroy() {
    this.docWatcher?.destroy();
    this.coachAppliedSub?.unsubscribe();
    this.coachAppliedSub = null;
  }

  storeIntoOldSubslot(subSlotId: string) {
    let currentSlot = this.fitnessPlan.subSlots[subSlotId];
    // if the exercise wasn't even used yet
    if (!currentSlot || !currentSlot.lastTimeConcluded_utc) {
      // do nothing
      return;
    }

    // if the exercise was already backed up
    let existingSubslot = findOldSubslot(this.fitnessState, subSlotId, currentSlot.exercise_id)
    if (existingSubslot) {
      // use that data
      deepOverwrite(existingSubslot, currentSlot);
      // if this is the first time it was switched away
    } else {
      // generate its backup
      if (!this.fitnessState.oldSubslots) {
        this.fitnessState.oldSubslots = [];
      }
      this.fitnessState.oldSubslots.push(deepClone(currentSlot));
    }
  }

}
